import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Bot, ThumbsUp, ThumbsDown, Loader2, Lightbulb, Target, TrendingUp } from 'lucide-react';

const AIMappingSuggestions = ({ calculationId, accounts = [], onMappingUpdate }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [processingActions, setProcessingActions] = useState({});

  const API = (process.env.REACT_APP_BACKEND_URL || "") || 'http://localhost:8001';

  useEffect(() => {
    if (calculationId) {
      loadSuggestionStats();
    }
  }, [calculationId]);

  const loadSuggestionStats = async () => {
    try {
      const response = await fetch(`${API}/api/client-patterns/analytics/current_client`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error loading suggestion stats:', err);
    }
  };

  const getAISuggestions = async () => {
    if (!calculationId) {
      setError('No calculation selected');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Get unmapped accounts from current calculation
      const unmappedAccounts = accounts.filter(account => !account.pool || account.pool === 'Untagged');
      
      if (unmappedAccounts.length === 0) {
        setError('No unmapped accounts found. All accounts appear to be already tagged.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API}/api/client-patterns/auto-map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accounts: unmappedAccounts.slice(0, 20), // Limit to first 20 for performance
          calculation_id: calculationId,
          client_id: 'current_client',
          confidence_threshold: 0.5
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuggestions(result.suggestions || []);
        if (result.suggestions?.length === 0) {
          setError('No high-confidence suggestions available. The AI needs more examples to learn your patterns.');
        }
      } else {
        throw new Error(result.message || result.detail || 'Failed to get AI suggestions');
      }
    } catch (err) {
      console.error('Error getting AI suggestions:', err);
      setError(`Failed to get AI suggestions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = async (suggestion, action = 'accept') => {
    const actionId = `${suggestion.account_id}_${action}`;
    setProcessingActions(prev => ({ ...prev, [actionId]: true }));
    
    try {
      if (action === 'accept') {
        // Apply the mapping to the account
        const response = await fetch(`${API}/api/accounts/${suggestion.account_id}/mapping`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pool: suggestion.suggested_pool,
            mapping_source: 'ai_suggestion',
            confidence: suggestion.confidence,
            calculation_id: calculationId
          })
        });

        if (!response.ok) {
          throw new Error('Failed to apply mapping');
        }
      }
      
      // Record the correction for learning (both accept and reject are valuable)
      await fetch(`${API}/api/client-patterns/record-correction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calculation_id: calculationId,
          account_id: suggestion.account_id,
          account_name: suggestion.account_name,
          account_number: suggestion.account_number,
          original_mapping: null,
          corrected_mapping: action === 'accept' ? suggestion.suggested_pool : null,
          correction_type: action === 'accept' ? 'ai_suggestion_accepted' : 'ai_suggestion_rejected',
          ai_confidence: suggestion.confidence,
          user_feedback: action
        })
      });
      
      // Remove suggestion from list
      setSuggestions(prev => prev.filter(s => s.account_id !== suggestion.account_id));
      
      // Notify parent component of mapping update
      if (onMappingUpdate && action === 'accept') {
        onMappingUpdate({
          account_id: suggestion.account_id,
          pool: suggestion.suggested_pool,
          source: 'ai_suggestion'
        });
      }
      
    } catch (err) {
      console.error(`Error ${action}ing suggestion:`, err);
      setError(`Failed to ${action} suggestion: ${err.message}`);
    } finally {
      setProcessingActions(prev => {
        const newState = { ...prev };
        delete newState[actionId];
        return newState;
      });
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'bg-green-100 text-green-700 border-green-200';
    if (confidence >= 0.75) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-orange-100 text-orange-700 border-orange-200';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.9) return 'Very High';
    if (confidence >= 0.75) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold">AI Mapping Suggestions</h3>
            </div>
            <Button 
              onClick={getAISuggestions} 
              disabled={loading || !calculationId}
              variant="outline"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Get AI Suggestions
                </>
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            AI analyzes your account patterns and suggests optimal pool assignments
          </p>
        </CardHeader>
        <CardContent>
          {/* Stats Section */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.patterns_learned || 0}
                </div>
                <div className="text-xs text-gray-600">Patterns Learned</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.automation_rate || 0}%
                </div>
                <div className="text-xs text-gray-600">Automation Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.accuracy || 0}%
                </div>
                <div className="text-xs text-gray-600">AI Accuracy</div>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Suggestions List */}
          {suggestions.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">
                  {suggestions.length} AI suggestions found
                </span>
              </div>
              
              {suggestions.map((suggestion) => (
                <Card key={suggestion.account_id} className="border-l-4 border-l-blue-400">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="font-medium text-gray-900">
                            {suggestion.account_number} - {suggestion.account_name}
                          </div>
                          <Badge 
                            variant="outline" 
                            className={getConfidenceColor(suggestion.confidence)}
                          >
                            {getConfidenceLabel(suggestion.confidence)} ({(suggestion.confidence * 100).toFixed(0)}%)
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-1">
                          <strong>AI suggests:</strong> 
                          <span className="ml-2 font-medium text-blue-700">
                            {suggestion.suggested_pool}
                          </span>
                        </div>
                        
                        {suggestion.amount && (
                          <div className="text-sm text-gray-600 mb-1">
                            <strong>Amount:</strong> ${suggestion.amount.toLocaleString()}
                          </div>
                        )}
                        
                        {suggestion.reasoning && (
                          <div className="text-sm text-gray-600 mb-2">
                            <strong>Reasoning:</strong> {suggestion.reasoning}
                          </div>
                        )}
                        
                        {suggestion.similar_accounts && suggestion.similar_accounts.length > 0 && (
                          <div className="text-xs text-gray-500">
                            Based on similar accounts: {suggestion.similar_accounts.slice(0, 2).join(', ')}
                            {suggestion.similar_accounts.length > 2 && ` and ${suggestion.similar_accounts.length - 2} more`}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <Button 
                          size="sm" 
                          onClick={() => applySuggestion(suggestion, 'accept')}
                          disabled={processingActions[`${suggestion.account_id}_accept`]}
                        >
                          {processingActions[`${suggestion.account_id}_accept`] ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <ThumbsUp className="w-3 h-3" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => applySuggestion(suggestion, 'reject')}
                          disabled={processingActions[`${suggestion.account_id}_reject`]}
                        >
                          {processingActions[`${suggestion.account_id}_reject`] ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <ThumbsDown className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : !loading && !error && (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No AI suggestions available</p>
              <p className="text-sm text-gray-500">
                Click "Get AI Suggestions" to see AI-powered mapping recommendations for unmapped accounts
              </p>
            </div>
          )}

          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">AI is analyzing your accounts...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIMappingSuggestions;
